import type { AppType } from '@lucimark/api/src/hono-app-type'
import type { TPlayerRegistrationResponse } from '@lucimark/shared/database/players-directory-schema'
import type { TAssetWithPlaylistsAndTags } from '@lucimark/shared/database/tenant-schema'
import type { ICachedAsset } from '@lucimark/shared/models/asset'
import type { TAudienceWebRtcSignalRequestBody } from '@lucimark/shared/models/audience-webrtc-signal'
import type { IPlaybackHeartbeatBody } from '@lucimark/shared/models/playback-heartbeat'
import type { TPlaybackHeartbeatDirectiveResponse } from '@lucimark/shared/models/player-overlay-ui'
import type {
  IPlayerRegistrationPayload,
  IServerTimeResponse
} from '@lucimark/shared/models/player-registration-payload'
import type { TPlayerRemoteCommandResult, TPlayerRemoteFrame, TPlayerRemoteFrameAck } from '@lucimark/shared/models/player-remote-control'
import { playerRemoteFrameAckSchema } from '@lucimark/shared/models/player-remote-control'
import type { TPlayerDiagnosticCommandResult } from '@lucimark/shared/models/player-remote-diagnostics'
import type { IWebOtaUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { Axios, AxiosHeaders, AxiosProgressEvent, AxiosResponse } from 'axios'
import type { TAuthenticationService } from './AuthenticationService'
import type { RegistrationStatusApiRaw } from '@/adapters'
import { parsePlayerAuthErrorBody } from '@lucimark/shared/models/player-auth-error'
import { playbackHeartbeatDirectiveResponseSchema } from '@lucimark/shared/models/player-overlay-ui'
import axios, { AxiosError } from 'axios'
import { hc } from 'hono/client'
import { parseRegistrationStatus } from '@/adapters'
import { buildPlaybackHeartbeatRequestBody } from '@/helpers/buildPlaybackHeartbeatRequestBody'
import { resolveAndroidDistribution } from '@/helpers/androidDistribution'
import {
  readHybridPlaybackFileAsBlob,
  stageHybridPlaybackFromUrl
} from '@/helpers/androidHybridPlayback'
import { getEmbeddedPlayerAppVersion } from '@/helpers/embeddedPlayerAppVersion'
import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { PLAYER_AUTH_SOFT_RETRY_CODES } from '@/helpers/playerRegistrationAuthPolicy'
import { parseRetryAfterHeaderMs } from '@/helpers/retryAfterMs'
import { PLAYBACK_HEARTBEAT_FETCH_TIMEOUT_MS } from '@/helpers/withTimeout'

/** Hono RPC client type. */
export type TAPIClient = ReturnType<typeof hc<AppType>>
export type TAPIClientAxiosProgressEvent = AxiosProgressEvent

/** Cancel the reader and reject if no chunk arrives within this window. */
const STREAM_STALL_TIMEOUT_MS = 30_000

function readChunkWithStallTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number
): Promise<ReadableStreamReadResult<Uint8Array>> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      void reader.cancel()
      reject(new Error('Asset stream stalled'))
    }, timeoutMs)
    reader.read().then(
      (result) => { clearTimeout(timer); resolve(result) },
      (err) => { clearTimeout(timer); reject(err) }
    )
  })
}

export class AssetNotFoundError extends Error {
  constructor(assetUrn: string) {
    super(assetUrn)
    this.name = 'AssetNotFoundError'
    Object.setPrototypeOf(this, AssetNotFoundError.prototype)
  }
}

export class ApiClientServiceError extends Error {
  readonly httpStatus: number
  /** Suggested wait before retry (e.g. from `Retry-After` on 429). */
  readonly retryAfterMs: number | undefined

  constructor(status: number, msg?: string, retryAfterMs?: number) {
    super(`status: ${status} ${msg ?? ''}`)
    this.name = 'ApiClientServiceError'
    this.httpStatus = status
    this.retryAfterMs = retryAfterMs
    Object.setPrototypeOf(this, ApiClientServiceError.prototype)
  }
}

export class ApiClientServiceUnauthorizedError extends Error {
  readonly code: string | undefined
  readonly httpStatus: number | undefined

  constructor(message?: string, code?: string, httpStatus?: number) {
    super(message ?? (code != null ? `unauthorized:${code}` : 'unauthorized'))
    this.name = 'ApiClientServiceUnauthorizedError'
    this.code = code
    this.httpStatus = httpStatus
    Object.setPrototypeOf(this, ApiClientServiceUnauthorizedError.prototype)
  }
}

async function parseUnauthorizedCodeFromResponseAsync(res: Response): Promise<string | undefined> {
  const ct = res.headers.get('content-type') ?? ''
  if (ct.includes('json')) {
    try {
      const data: unknown = await res.clone().json()
      return parsePlayerAuthErrorBody(data)?.code
    }
    catch {
      return undefined
    }
  }
  // Proxies sometimes omit or mislabel Content-Type; still try JSON for 401/403 bodies.
  try {
    const data: unknown = await res.clone().json()
    return parsePlayerAuthErrorBody(data)?.code
  }
  catch {
    return undefined
  }
}

function parseUnauthorizedCodeFromAxiosData(data: unknown): string | undefined {
  if (data == null) { return undefined }
  if (typeof data === 'object') {
    return parsePlayerAuthErrorBody(data)?.code
  }
  if (typeof data === 'string') {
    try {
      return parsePlayerAuthErrorBody(JSON.parse(data) as unknown)?.code
    }
    catch {
      return undefined
    }
  }
  return undefined
}

/** POST attempts (initial + retries) for transient 5xx / 429 / 408 or network errors. */
const PLAYBACK_HEARTBEAT_POST_MAX_ATTEMPTS = 4
const PLAYBACK_HEARTBEAT_POST_BASE_BACKOFF_MS = 500
/** Initial + retries when asset download returns 429/503 with Retry-After. */
const ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS = 3

function sleepMsForPlaybackHeartbeat(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function sleepMs(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/** `fetch` AbortSignal / AbortController timeout (DOMException or Error named AbortError). */
function isPlaybackHeartbeatAbortError(err: unknown): boolean {
  return err != null
    && typeof err === 'object'
    && 'name' in err
    && (err as { name?: unknown }).name === 'AbortError'
}

function isPlaybackHeartbeatPostTransientFailure(err: unknown): boolean {
  if (err instanceof TypeError) {
    return true
  }
  if (isPlaybackHeartbeatAbortError(err)) {
    return true
  }
  if (err instanceof ApiClientServiceError) {
    const s = err.httpStatus
    return s === 408 || s === 429 || (s >= 500 && s <= 599)
  }
  return false
}

export type { IAssetPlay, IPlaybackHeartbeatBody as IPlaybackHeartbeatOptions } from '@lucimark/shared/models/playback-heartbeat'

export interface IAPIClientService {
  fetchRegistration: (opts?: { hardwareCapacity?: import('@lucimark/shared/models/playback-heartbeat').IHardwareCapacitySnapshot, omitTenantCredentials?: boolean }) => Promise<TPlayerRegistrationResponse & { webOta?: IWebOtaUpdateEventPayload | null }>
  fetchRegistrationStatus: () => Promise<IPlayerRegistrationPayload | null>
  /** Soft-retry, then re-fetch registration from Players Directory without tenant headers (secret rotation / offline delivery). */
  fetchRegistrationStatusWithStaleCredentialRecovery: () => Promise<IPlayerRegistrationPayload | null>
  /** Fetches server time for clock sync; pass clientSendMs for RTT-corrected offset. */
  fetchServerTime: (clientSendMs?: number) => Promise<IServerTimeResponse>
  reportVersionUpdate: (version: string) => Promise<void>
  fetchAsset: (urn: string) => Promise<TAssetWithPlaylistsAndTags>
  fetchAssetBinary: (urn: string, cachedEtag?: string, onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void) => Promise<ICachedAsset | null>
  setIsPooling: (isPooling: boolean) => Promise<void>
  /** Clears `pooling=true` on registration HTTP immediately when the WebSocket is live (see PoolingService debounce). */
  clearPoolingHttpQueryFlag: () => void
  setPlayerOffline: () => Promise<void>
  /** Best-effort PATCH offline with keepalive for page unload (e.g. pagehide). */
  reportOfflineKeepalive: () => Promise<void>
  /**
   * Report that the player is actively playing content (for uptime) and optionally send storage used % and proof-of-play data.
   * Call periodically while showing campaign (e.g. every 60s from VBuffer). Optional opts.storageUsedPercent (0–100)
   * is sent as JSON body and persisted on the player row for the app players view. When proofOfPlay is enabled,
   * pass assetPlays and playlistUrn for proof-of-play reporting. See docs/player-storage.md and docs/proof-of-play.md.
   */
  reportPlaybackHeartbeat: (opts?: IPlaybackHeartbeatBody) => Promise<TPlaybackHeartbeatDirectiveResponse | null>
  /**
   * Same as {@link reportPlaybackHeartbeat} with exponential backoff retries for transient 5xx / 429 / 408
   * and network errors (`TypeError` from `fetch`).
   */
  reportPlaybackHeartbeatWithRetries: (opts?: IPlaybackHeartbeatBody) => Promise<TPlaybackHeartbeatDirectiveResponse | null>
  uploadLivePreview: (blob: Blob) => Promise<void>
  /** WebRTC signaling (answer / ICE) back to the operator session. */
  postAudienceWebRtcSignal: (body: TAudienceWebRtcSignalRequestBody) => Promise<void>
}

export class APIClientService implements IAPIClientService {
  /** Resolved at request time so initPlatform() can run first (e.g. Android 10.0.2.2 override). */
  private _getApiURL: () => string
  private _client: TAPIClient | null = null
  private _authenticationService: TAuthenticationService
  private _axios: Axios | null = null
  private _pooling: boolean = false
  private _getAppVersion: () => string | Promise<string>
  /** Resolved at request time so initPlatform() can run first (e.g. for mac). */
  private _getPlatform: () => string
  /** Web bundle id (commit hash on web, OTA `.version` on Tauri); mirrors WebSocket `x-player-web-version`. */
  private _getWebVersion: (() => string | null | Promise<string | null>) | null

  constructor(params: {
    apiURL: string | (() => string)
    authenticationService: TAuthenticationService
    /** Sent as `x-player-version`; may be async (native + embedded merge on Tauri). */
    getAppVersion: () => string | Promise<string>
    platform: string | (() => string)
    getWebVersion?: () => string | null | Promise<string | null>
  }) {
    const { apiURL, authenticationService, getAppVersion, platform, getWebVersion } = params
    this._getApiURL = typeof apiURL === 'function' ? apiURL : () => apiURL
    this._authenticationService = authenticationService
    this._getAppVersion = getAppVersion
    this._getPlatform = typeof platform === 'function' ? platform : () => platform
    this._getWebVersion = getWebVersion ?? null
  }

  private _ensureClient(): void {
    if (this._client != null) { return }
    const baseURL = this._getApiURL()
    this._client = hc<AppType>(baseURL, {
      headers: this.getHeaders.bind(this),
      fetch: (input: RequestInfo | URL, requestInit?: RequestInit) => {
        return fetch(input, {
          method: requestInit?.method ?? 'GET',
          headers: requestInit?.headers,
          body: requestInit?.body ?? null
        }).then(async (res) => {
          if ([401, 403].includes(res.status)) {
            const code = await parseUnauthorizedCodeFromResponseAsync(res)
            throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
          }
          return res
        }).catch((err: unknown) => {
          throw err
        })
      }
    })
    this._axios = axios.create({ baseURL, adapter: 'fetch' })
    this._axios.interceptors.request.use(async (config) => {
      config.headers = { ...config.headers, ...(await this.getHeaders()) } as AxiosHeaders
      return config
    })
    this._axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error instanceof AxiosError) {
          if ([401, 403].includes(error.status ?? -1)) {
            const code = parseUnauthorizedCodeFromAxiosData(error.response?.data)
            throw new ApiClientServiceUnauthorizedError(undefined, code, error.status ?? undefined)
          }
          else {
            throw new ApiClientServiceError(error.status ?? -1, error.message)
          }
        }
        throw error
      }
    )
  }

  async fetchRegistration(opts?: { hardwareCapacity?: IPlaybackHeartbeatBody['hardwareCapacity'], omitTenantCredentials?: boolean }) {
    this._ensureClient()
    const pooling = this._pooling ? 'true' : undefined
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/registration${pooling != null ? `?pooling=${pooling}` : ''}`
    const headers = opts?.omitTenantCredentials === true
      ? await this._getPlayerDeviceHeadersOnly()
      : await this.getHeaders()
    if (opts?.hardwareCapacity != null) {
      headers['x-player-hardware-capacity'] = btoa(JSON.stringify(opts.hardwareCapacity))
    }
    const res = await fetch(url, { method: 'GET', headers })
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
    const registration = await res.json() as TPlayerRegistrationResponse & { webOta?: IWebOtaUpdateEventPayload | null }
    return registration
  }

  /**
   * Soft-retry once for transient / unknown `code` (proxy may strip JSON), then re-sync `{ tenantId, secret }`
   * from GET /registration without tenant headers on any further 401/403 while storage still has a tenant.
   */
  async fetchRegistrationStatusWithStaleCredentialRecovery(): Promise<IPlayerRegistrationPayload | null> {
    this._ensureClient()
    const tryStatus = (): Promise<IPlayerRegistrationPayload | null> => this.fetchRegistrationStatus()
    const afterDirectoryRefresh = async (): Promise<IPlayerRegistrationPayload | null> => {
      const updated = await this._authenticationService.getRegistration()
      if (updated?.tenantId == null) {
        return null
      }
      return await tryStatus()
    }

    try {
      return await tryStatus()
    }
    catch (e) {
      if (!(e instanceof ApiClientServiceUnauthorizedError)) {
        throw e
      }
      const u = e
      if (u.httpStatus !== 401 && u.httpStatus !== 403) {
        throw e
      }

      const allowIdenticalRetry = u.code == null || PLAYER_AUTH_SOFT_RETRY_CODES.has(u.code)

      if (allowIdenticalRetry) {
        try {
          return await tryStatus()
        }
        catch (e2) {
          if (!(e2 instanceof ApiClientServiceUnauthorizedError)) {
            throw e2
          }
          if (e2.httpStatus !== 401 && e2.httpStatus !== 403) {
            throw e2
          }
          if (await this._tryRefreshRegistrationFromPlayersDirectoryAfterAuthFailure()) {
            return await afterDirectoryRefresh()
          }
          throw e2
        }
      }

      if (await this._tryRefreshRegistrationFromPlayersDirectoryAfterAuthFailure()) {
        return await afterDirectoryRefresh()
      }
      throw e
    }
  }

  async fetchRegistrationStatus(): Promise<IPlayerRegistrationPayload | null> {
    this._ensureClient()
    const pooling = this._pooling ? 'true' : undefined
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    // Cache-bust so we always get fresh config (widgetConfig at campaign/playlist/frame) after publish; otherwise the browser can return a cached response and the player shows stale config.
    const query = { pooling, _: String(Date.now()) } as { pooling?: string, _?: string }
    if (pooling === undefined) { delete query.pooling }
    const res = await this._client!.players[':b64PlayerId']['registration-status'].$get({ param: { b64PlayerId }, query })
    if ([401, 403].includes(res.status)) {
      const body = await res.json().catch(() => null) as { code?: string } | null
      throw new ApiClientServiceUnauthorizedError(undefined, body?.code, res.status)
    }
    if (res.status === 429) {
      const retryAfterMs = parseRetryAfterHeaderMs(res.headers.get('Retry-After'))
      throw new ApiClientServiceError(429, 'registration_status_rate_limited', retryAfterMs)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
    const raw = await res.json() as RegistrationStatusApiRaw | null
    return parseRegistrationStatus(raw)
  }

  async fetchServerTime(clientSendMs?: number): Promise<IServerTimeResponse> {
    this._ensureClient()
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const query = clientSendMs != null && Number.isFinite(clientSendMs)
      ? { clientSendMs: String(clientSendMs) }
      : {}
    const res = await this._client!.players[':b64PlayerId'].time.$get({
      param: { b64PlayerId },
      query
    })
    return res.json() as Promise<IServerTimeResponse>
  }

  async reportVersionUpdate(version: string) {
    this._ensureClient()
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const b64Version = btoa(version)
    await this._client!.players[':b64PlayerId'].updates[':b64Version'].$patch({ param: { b64PlayerId, b64Version } })
  }

  async fetchAsset(urn: string) {
    this._ensureClient()
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const b64URN = btoa(urn)
    const res = await this._client!.players[':b64PlayerId'].assets[':b64URN'].$get({ param: { b64PlayerId, b64URN } })
    if (!res.ok) {
      if (res.status === 404) {
        throw new AssetNotFoundError(urn)
      }
      else {
        console.error(res)
        throw new Error('error fetching asset')
      }
    }
    const raw = await res.json() as TAssetWithPlaylistsAndTags & { createdAt: string, updatedAt: string }
    return {
      ...raw,
      createdAt: new Date(raw.createdAt),
      updatedAt: new Date(raw.updatedAt)
    } as TAssetWithPlaylistsAndTags
  }

  async fetchAssetBinary(urn: string, cachedEtag?: string, onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void): Promise<ICachedAsset | null> {
    this._ensureClient()
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const b64URN = btoa(urn)
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/assets/${b64URN}/download`
    const headers: Record<string, string> = await this.getHeaders()
    if (cachedEtag !== undefined) {
      headers['If-None-Match'] = cachedEtag
    }

    // P2 (#1586): Android Tauri downloads progressive MP4 straight to hybrid-cache (no IDB blob).
    // Old APKs / non-mp4 / native failure → fall through to WebView fetch below.
    if (isTauriRuntime() && this._getPlatform() === 'android') {
      for (let attempt = 0; attempt < ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS; attempt++) {
        const native = await stageHybridPlaybackFromUrl({
          key: urn,
          url,
          headers,
          ...(cachedEtag != null && cachedEtag !== '' ? { ifNoneMatch: cachedEtag } : {})
        })
        if (native == null) {
          break
        }
        if (native.notModified === true) {
          // 304 with a live hybrid-cache path → keep IndexedDB row; missing path → fall through to full GET.
          if (typeof native.path === 'string' && native.path !== '') {
            onDownloadProgress?.({ progress: 1, loaded: 0, total: 0 } as AxiosProgressEvent)
            return null
          }
          break
        }
        const status = native.httpStatus
        if (status === 401 || status === 403) {
          throw new ApiClientServiceUnauthorizedError(undefined, undefined, status)
        }
        if (status === 429 || status === 503) {
          const retryAfterMs = parseRetryAfterHeaderMs(undefined)
          const label = status === 429 ? 'rate limited' : 'unavailable'
          if (attempt >= ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS - 1) {
            throw new ApiClientServiceError(status, label, retryAfterMs)
          }
          console.warn(
            `[APIClientService] native asset download ${status} for ${urn}; waiting ${retryAfterMs}ms before retry ${attempt + 2}/${ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS}`
          )
          await sleepMs(retryAfterMs)
          continue
        }
        if (native.ok !== true || native.path == null || native.path === '') {
          if (status != null && status >= 400) {
            throw new ApiClientServiceError(status)
          }
          break
        }
        const etag = native.etag ?? ''
        const contentType = (native.contentType ?? '').trim()
        const byteLength = typeof native.byteLength === 'number' ? native.byteLength : 0
        if (native.kind === 'mp4') {
          onDownloadProgress?.({ progress: 1, loaded: byteLength, total: byteLength } as AxiosProgressEvent)
          return {
            urn,
            etag,
            lastAccess: new Date(),
            size: byteLength,
            type: contentType.includes('video') ? contentType : 'video/mp4',
            nativeFilePath: native.path
          }
        }
        // Non-mp4: hydrate once from disk so images/widgets still land in IDB as Blobs.
        const mime = contentType || 'application/octet-stream'
        const blob = await readHybridPlaybackFileAsBlob(urn, mime)
        if (blob != null && blob.size > 0) {
          onDownloadProgress?.({ progress: 1, loaded: blob.size, total: blob.size } as AxiosProgressEvent)
          return { urn, etag, blob, lastAccess: new Date(), size: blob.size, type: blob.type || mime }
        }
        break
      }
    }

    // Use native fetch so we can inspect the status code before the browser
    // attempts to read the response body. Axios with adapter:'fetch' tries to
    // deserialise the body as a Blob for every response — including 304 Not
    // Modified — which the Fetch spec forbids (304 is a "null body status"),
    // causing a "Response constructor: Response body is given with a null body
    // status" error that surfaces as ApiClientServiceError status:-1 and drops
    // the cached asset, leaving the player on a black screen on reload.
    for (let attempt = 0; attempt < ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS; attempt++) {
      const response = await fetch(url, { method: 'GET', headers })

      if (response.status === 401 || response.status === 403) {
        const code = await parseUnauthorizedCodeFromResponseAsync(response)
        throw new ApiClientServiceUnauthorizedError(undefined, code, response.status)
      }

      // Must check 304 before touching the body — reading the body of a null-body
      // status response throws in the browser.
      if (response.status === 304) {
        // Revalidation hit: count as complete so playlist rematch progress is not stuck at 0%.
        onDownloadProgress?.({ progress: 1, loaded: 0, total: 0 } as AxiosProgressEvent)
        return null
      }

      // 429 = per-player rate limit (always back off; default Retry-After if header missing).
      // 503 with Retry-After = kill switch / temporary unavailability — honor the header.
      // Bare 503 (e.g. tenant_not_ready) must fail immediately so AssetsService can fall back
      // to cache without stacking multi-minute sleeps.
      if (response.status === 429 || response.status === 503) {
        const retryAfterHeader = response.headers.get('Retry-After')
        const hasRetryAfter = retryAfterHeader != null && retryAfterHeader.trim() !== ''
        if (response.status === 503 && !hasRetryAfter) {
          throw new ApiClientServiceError(503, 'unavailable')
        }
        const retryAfterMs = parseRetryAfterHeaderMs(retryAfterHeader)
        const label = response.status === 429 ? 'rate limited' : 'unavailable'
        if (attempt >= ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS - 1) {
          throw new ApiClientServiceError(response.status, label, retryAfterMs)
        }
        console.warn(
          `[APIClientService] asset download ${response.status} for ${urn}; waiting ${retryAfterMs}ms (Retry-After) before retry ${attempt + 2}/${ASSET_BINARY_RETRYABLE_MAX_ATTEMPTS}`
        )
        await sleepMs(retryAfterMs)
        continue
      }

      if (!response.ok) {
        throw new ApiClientServiceError(response.status)
      }

      const etag: string = response.headers.get('etag') ?? ''
      const contentType = response.headers.get('content-type') ?? ''
      const contentLength = Number(response.headers.get('content-length')) || 0

      // Stream the body so we can emit download-progress events.
      if (onDownloadProgress && response.body != null && contentLength > 0) {
        const reader = response.body.getReader()
        const chunks: Uint8Array[] = []
        let received = 0
        while (true) {
          const chunk = await readChunkWithStallTimeout(reader, STREAM_STALL_TIMEOUT_MS)
          if (chunk.done) { break }
          chunks.push(chunk.value)
          received += chunk.value.length
          onDownloadProgress({ progress: received / contentLength, loaded: received, total: contentLength } as AxiosProgressEvent)
        }
        // Stream chunks are Uint8Array<ArrayBufferLike>; Blob accepts BlobPart[] (ArrayBuffer-backed). Runtime is safe.
        const blob = new Blob(chunks as BlobPart[], { type: contentType })
        return { urn, etag, blob, lastAccess: new Date(), size: blob.size, type: blob.type }
      }

      // No Content-Length (common on some local/CDN paths): cannot report mid-transfer %.
      // Emit completion so AssetsService does not leave the job at 0% until the whole batch ends.
      const blob = await response.blob()
      onDownloadProgress?.({
        progress: 1,
        loaded: blob.size,
        total: blob.size > 0 ? blob.size : undefined
      } as AxiosProgressEvent)
      return { urn, etag, blob, lastAccess: new Date(), size: blob.size, type: blob.type }
    }

    throw new ApiClientServiceError(429, 'rate limited')
  }

  /**
   * Observability PATCH only — always use device headers (no tenant bearer).
   * Authenticated calls would 403 with stale local credentials after rotate/reload; the tenant row’s
   * `pooling` flag is updated from `GET …/registration-status` via `setPlayerOnline({ pooling })` instead.
   */
  clearPoolingHttpQueryFlag(): void {
    this._pooling = false
  }

  async setIsPooling(isPooling: boolean) {
    this._ensureClient()
    this._pooling = isPooling
    await this._playerPatchNoContent('/pooling', { state: isPooling }, await this._getPlayerDeviceHeadersOnly())
  }

  async setPlayerOffline() {
    this._ensureClient()
    await this._playerPatchWithStaleTenantAuthFallback('/offline', undefined)
  }

  async reportOfflineKeepalive() {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/offline`
    try {
      const headers = await this.getHeaders()
      const hadTenantAuth = headers['x-tenant-id'] != null
      const res = await fetch(url, { method: 'PATCH', keepalive: true, headers })
      if (hadTenantAuth && [401, 403].includes(res.status)) {
        await fetch(url, {
          method: 'PATCH',
          keepalive: true,
          headers: await this._getPlayerDeviceHeadersOnly()
        })
      }
    }
    catch {
      // best-effort — same as ignoring failed keepalive
    }
  }

  /**
   * Sends POST playback-heartbeat with optional JSON body for uptime and proof-of-play.
   * Returns overlay directive when the server responds 200 with a validated body; null for 204 or unparseable body.
   */
  async reportPlaybackHeartbeat(opts?: IPlaybackHeartbeatBody): Promise<TPlaybackHeartbeatDirectiveResponse | null> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const body = buildPlaybackHeartbeatRequestBody(opts)
    const headers = await this.getHeaders()
    headers['Content-Type'] = 'application/json'
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/playback-heartbeat`
    const controller = new AbortController()
    const abortTimer = setTimeout(() => controller.abort(), PLAYBACK_HEARTBEAT_FETCH_TIMEOUT_MS)
    let res: Response
    try {
      res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal
      })
    }
    finally {
      clearTimeout(abortTimer)
    }
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
    if (res.status === 204) {
      return null
    }
    const text = await res.text()
    if (text.length === 0) {
      return null
    }
    try {
      const json: unknown = JSON.parse(text)
      const parsed = playbackHeartbeatDirectiveResponseSchema.safeParse(json)
      return parsed.success ? parsed.data : null
    }
    catch {
      return null
    }
  }

  async reportPlaybackHeartbeatWithRetries(opts?: IPlaybackHeartbeatBody): Promise<TPlaybackHeartbeatDirectiveResponse | null> {
    for (let attempt = 0; attempt < PLAYBACK_HEARTBEAT_POST_MAX_ATTEMPTS; attempt++) {
      try {
        return await this.reportPlaybackHeartbeat(opts)
      }
      catch (err) {
        const hasMoreAttempts = attempt < PLAYBACK_HEARTBEAT_POST_MAX_ATTEMPTS - 1
        if (!hasMoreAttempts || !isPlaybackHeartbeatPostTransientFailure(err)) {
          throw err
        }
        const delayMs = PLAYBACK_HEARTBEAT_POST_BASE_BACKOFF_MS * 2 ** attempt
        console.warn(
          JSON.stringify({
            tag: 'playback_heartbeat_post_retry',
            attempt,
            delayMs,
            message: err instanceof Error ? err.message : String(err)
          })
        )
        await sleepMsForPlaybackHeartbeat(delayMs)
      }
    }
    throw new Error('playback heartbeat retries exhausted')
  }

  async uploadLivePreview(blob: Blob): Promise<void> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const headers = await this.getHeaders()
    headers['Content-Type'] = blob.type || 'image/jpeg'
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/live-preview`
    const res = await fetch(url, { method: 'PUT', headers, body: blob })
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
  }

  async postAudienceWebRtcSignal(body: TAudienceWebRtcSignalRequestBody): Promise<void> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const headers = await this.getHeaders()
    headers['Content-Type'] = 'application/json'
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/audience-webrtc/signal`
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) })
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
  }

  async postPlayerDiagnosticCommandResult(body: TPlayerDiagnosticCommandResult): Promise<void> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const headers = await this.getHeaders()
    headers['Content-Type'] = 'application/json'
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/diagnostics/command-result`
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) })
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
  }

  async postPlayerRemoteCommandResult(body: TPlayerRemoteCommandResult): Promise<void> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const headers = await this.getHeaders()
    headers['Content-Type'] = 'application/json'
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/remote/command-result`
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) })
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
  }

  /**
   * Pushes one Remote View frame. Throws {@link ApiClientServiceError} with the HTTP status so the
   * streamer can tell "session over" (409/404) from a transient failure.
   */
  async postPlayerRemoteFrame(frame: TPlayerRemoteFrame): Promise<TPlayerRemoteFrameAck> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const headers = await this.getHeaders()
    headers['Content-Type'] = 'application/json'
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}/remote/frame`
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(frame) })
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      const retryAfterMs = res.status === 429
        ? parseRetryAfterHeaderMs(res.headers.get('Retry-After'), Date.now(), { defaultMs: 5_000, maxMs: 30_000 })
        : undefined
      throw new ApiClientServiceError(res.status, undefined, retryAfterMs)
    }
    const parsed = playerRemoteFrameAckSchema.safeParse(await res.json())
    // An unreadable ack must not look like "nobody is watching" — assume a viewer and keep going.
    return parsed.success ? parsed.data : { viewers: 1 }
  }

  private async _playerPatchNoContent(
    pathSuffix: string,
    jsonBody: Record<string, unknown> | undefined,
    headers: Record<string, string>
  ): Promise<void> {
    const b64PlayerId = btoa(await this._authenticationService.getPlayerId())
    const base = this._getApiURL().replace(/\/$/, '')
    const url = `${base}/players/${b64PlayerId}${pathSuffix}`
    const merged: Record<string, string> = { ...headers }
    const init: RequestInit = { method: 'PATCH', headers: merged }
    if (jsonBody !== undefined) {
      merged['Content-Type'] = 'application/json'
      init.body = JSON.stringify(jsonBody)
    }
    const res = await fetch(url, init)
    if ([401, 403].includes(res.status)) {
      const code = await parseUnauthorizedCodeFromResponseAsync(res)
      throw new ApiClientServiceUnauthorizedError(undefined, code, res.status)
    }
    if (!res.ok) {
      throw new ApiClientServiceError(res.status)
    }
  }

  /** Prefer tenant-authenticated PATCH; on any 401/403 with tenant auth, retry device-only (parse errors can leave `code` undefined). */
  private async _playerPatchWithStaleTenantAuthFallback(
    pathSuffix: string,
    jsonBody: Record<string, unknown> | undefined
  ): Promise<void> {
    const authedHeaders = await this.getHeaders()
    const hadTenantAuth = authedHeaders['x-tenant-id'] != null
    try {
      await this._playerPatchNoContent(pathSuffix, jsonBody, authedHeaders)
    }
    catch (e) {
      const st = e instanceof ApiClientServiceUnauthorizedError ? e.httpStatus : undefined
      if (
        hadTenantAuth
        && e instanceof ApiClientServiceUnauthorizedError
        && (st === 401 || st === 403)
      ) {
        await this._playerPatchNoContent(pathSuffix, jsonBody, await this._getPlayerDeviceHeadersOnly())
        return
      }
      throw e
    }
  }

  private async _getPlayerDeviceHeadersOnly(): Promise<Record<string, string>> {
    const playerId = await this._authenticationService.getPlayerId()
    const appVersion = await Promise.resolve(this._getAppVersion())
    const platform = this._getPlatform()
    const headers: Record<string, string> = {
      'x-player-id': playerId,
      'x-player-platform': platform,
      'x-player-version': appVersion,
      'X-Requested-With': 'com.lucimark.player'
    }
    const embeddedAppVersion = getEmbeddedPlayerAppVersion()
    if (embeddedAppVersion !== '') {
      headers['x-player-embedded-version'] = embeddedAppVersion
    }
    if (this._getWebVersion != null) {
      const webVer = await Promise.resolve(this._getWebVersion())
      if (webVer != null && webVer !== '') {
        headers['x-player-web-version'] = webVer
      }
    }
    if (platform === 'android') {
      const distribution = await resolveAndroidDistribution()
      if (distribution != null) {
        headers['x-player-android-distribution'] = distribution
      }
    }
    return headers
  }

  /** Re-read directory row (no tenant headers) and persist — fixes stale secret after rotate/reload and code-less 403 via proxy. */
  private async _tryRefreshRegistrationFromPlayersDirectoryAfterAuthFailure(): Promise<boolean> {
    const existing = await this._authenticationService.getRegistration()
    if (existing?.tenantId == null) {
      return false
    }
    const reg = await this.fetchRegistration({ omitTenantCredentials: true })
    // Assigned directory rows are one-shot: after consume, GET creates an unregistered pairing row — never overwrite local tenant credentials with that.
    if (reg.tenantId == null) {
      return false
    }
    await this._authenticationService.setRegistration(reg)
    return true
  }

  private async getHeaders() {
    const headers = await this._getPlayerDeviceHeadersOnly()
    const registration = await this._authenticationService.getRegistration()
    if (registration !== null && registration.tenantId !== null) {
      headers['x-tenant-id'] = registration.tenantId
      headers.authorization = `Bearer ${await this._authenticationService.getBearerToken()}`
    }
    return headers
  }
}

export type TAPIClientService = APIClientService
